import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { getInitials } from 'lib/text-display'
import { useVirtualCoachingEnabled } from 'lib/pga-coach/use-virtual-coaching'
import { useVirtualCoachingSummary } from 'pages/pga-coach/virtual-coaching/api-hooks'
import {
  countOverdue,
  getAgingLabel,
  getRequestPath,
  getRequestsPath,
  getStudentName,
  isOverdue,
  REQUEST_STATUS,
  REQUEST_TABS,
} from 'pages/pga-coach/virtual-coaching/utils'

export const VIRTUAL_COACHING_ITEM_TYPE = 'virtual-coaching'

export const VIRTUAL_COACHING_LIST_PATH = getRequestsPath(REQUEST_TABS.READY)

const byDueDate = (a, b) => {
  if (!a.dueAt && !b.dueAt) return 0
  if (!a.dueAt) return 1
  if (!b.dueAt) return -1
  return (
    DateTime.fromISO(a.dueAt).toMillis() - DateTime.fromISO(b.dueAt).toMillis()
  )
}

// One "Needs your attention" row for a golfer waiting on a swing analysis.
// `counts` carries the totals across all ready requests for analytics.
export const virtualCoachingItem = (request, counts = {}, now) => {
  const name = getStudentName(request.student)
  const overdue = isOverdue(request, now)

  return {
    key: `${VIRTUAL_COACHING_ITEM_TYPE}-${request.id}`,
    type: VIRTUAL_COACHING_ITEM_TYPE,
    name,
    initials: getInitials(name),
    meta: [
      request.offering?.title || 'Virtual swing analysis',
      request.dueAt ? getAgingLabel(request, now) : null,
    ]
      .filter(Boolean)
      .join(' · '),
    chip: overdue ? 'Overdue' : 'Virtual Coaching',
    overdue,
    requestId: request.id,
    readyCount: counts.readyCount,
    overdueCount: counts.overdueCount,
    path: getRequestPath(request.id),
  }
}

// The coach's ready-to-analyze requests as attention rows, most urgent first.
// Empty unless the feature is on (flag and Coach+).
export const useVirtualCoachingAttention = () => {
  const enabled = useVirtualCoachingEnabled()
  const { data, loading } = useVirtualCoachingSummary({ skip: !enabled })
  const requests = data?.virtualCoachingRequests

  const items = useMemo(() => {
    if (!enabled) return []
    const ready = (requests || [])
      .filter(request => request.status === REQUEST_STATUS.READY_TO_ANALYZE)
      .sort(byDueDate)
    const counts = {
      readyCount: ready.length,
      overdueCount: countOverdue(ready),
    }
    return ready.map(request => virtualCoachingItem(request, counts))
  }, [enabled, requests])

  return { items, loading: Boolean(enabled && loading && !data) }
}
